import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchMyAnswers } from '../api/client';
import type { Mode, ReusableAnswers } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { MODE_INFO } from '../diagnosis/labels';

/**
 * 入口ページの「前回の回答を使う」（doc/ASSESSMENT.md §4.8）。
 *
 * 設問は全テーマ・全モード共通なので、ログインして受けた結果があれば、別のテーマは答え直さずに結果が出る。
 * 選んだ深さに対して足りない設問があれば（「ふつう」の回答で「じっくり」を選んだときの副軸 24 問）、その分だけ答える。
 * 同じテーマを同じ深さで写しても結果は同じになるので、そのときは前回の結果へのリンクを出す。
 */
interface Props {
  readonly mode: Mode;
  readonly themeId: string;
}

/** 選んだ深さに足りない設問がいちばん少ないもの。同数なら新しいもの（一覧は新しい順）。 */
function pickSource(sources: readonly ReusableAnswers[], mode: Mode): ReusableAnswers | null {
  let best: ReusableAnswers | null = null;
  for (const source of sources) {
    if (!best || source.missing[mode] < best.missing[mode]) best = source;
  }
  return best;
}

const formatDate = (iso: string) => new Date(iso).toLocaleDateString('ja-JP', { year: 'numeric', month: 'short', day: 'numeric' });

export function ReuseAnswers({ mode, themeId }: Props) {
  const { ready, user, enabled } = useAuth();
  const navigate = useNavigate();
  const [sources, setSources] = useState<readonly ReusableAnswers[] | null>(null);

  useEffect(() => {
    if (!user) { setSources(null); return; }
    let cancelled = false;
    void fetchMyAnswers()
      .then((r) => { if (!cancelled) setSources(r.sources); })
      .catch(() => { if (!cancelled) setSources([]); }); // 読めなくても入口は使える
    return () => { cancelled = true; };
  }, [user]);

  const source = useMemo(() => (sources ? pickSource(sources, mode) : null), [sources, mode]);

  if (!ready || !enabled) return null;

  if (!user) {
    return (
      <p className="reuse reuse--hint">
        ログインして受けておくと、次からは別の診断を答え直さずに結果が見られます。
      </p>
    );
  }
  if (sources === null || !source) return null;

  const total = MODE_INFO.find((m) => m.mode === mode)?.count ?? 0;
  const missing = source.missing[mode];
  const sameTheme = source.themeId === themeId;
  const themeQuery = `theme=${encodeURIComponent(themeId)}&`;

  // 同じ診断を同じ深さで写しても同じ結果になるだけなので、前回の結果を案内する
  if (sameTheme && missing === 0 && source.mode === mode) {
    return (
      <p className="reuse">
        この深さは <Link to={`/r/${source.resultId}`}>{formatDate(source.createdAt)} に受けています</Link>。別の深さを選ぶか、もう一度答えて安定性を確かめることもできます。
      </p>
    );
  }

  return (
    <div className="reuse" role="status">
      <p className="reuse__lead">
        {formatDate(source.createdAt)} に受けた「{source.themeTitle}」の回答（{source.answered} 問）が使えます。
        {missing === 0
          ? ' 設問は共通なので、答えずに結果が見られます。'
          : ` ${total - missing} 問はその回答を使い、残り ${missing} 問だけ答えます。`}
      </p>
      <button
        className="button button--primary button--small"
        type="button"
        onClick={() => navigate(`/shindan?${themeQuery}mode=${mode}&from=${encodeURIComponent(source.resultId)}`)}
      >
        {missing === 0 ? '答えずに結果を見る' : `残り ${missing} 問だけ答える`}
      </button>
    </div>
  );
}
